import { GetEventResponseDto } from "@/api/event/dto";
import { useGetRandomTalkingPoints } from "@/api/event/useGetRandomTalkingPoints";
import { GetMyUpcomingReservationsResponseDto } from "@/api/generated/generatedTypes";
import { StyledActionButton } from "@/components/shared/StyledActionButton";
import { formatDateToMonthDay } from "@/utils/date";
import { Box, Button, Center, Flex, Group, Loader, Stack, Text } from "@mantine/core";
import { useDisclosure, useElementSize } from "@mantine/hooks";
import { lazy, Suspense, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { AttendanceStatusSelector } from "./AttendanceStatusSelector";
import FeedbackModal from "./FeedbackModal";
import { LineHeader } from "./LineHeader";
import { LineItem } from "./LineItem";
import { LineItemSubItem } from "./LineItemSubItem";

// Lazy load the PieChart component
const PieChart = lazy(() =>
  import("@mantine/charts").then((module) => ({ default: module.PieChart }))
);

const NICE_COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#E7E9ED",
  "#C9CBCF",
  "#77DD77",
  "#F7464A",
];

const NICE_COLORS_2 = [
  "#5DADE2",
  "#F7DC6F",
  "#E74C3C",
  "#82E0AA",
  "#AF7AC5",
  "#F39C12",
  "#48C9B0",
  "#EC7063",
  "#AAB7B8",
  "#5499C7",
];

const PROFESSION_DISPLAY_MAP: Record<string, string> = {
  IT: "IT",
  ART: "예술",
  MEDICAL: "의료",
  PUBLIC: "정치/공공",
  FNB: "요식업",
  BUSINESS: "사업",
  FINANCE: "금융",
  ETC: "기타",
};

function getGroupHintsUnlockDate(eventStartTime: string | undefined): string {
  if (!eventStartTime) return "전체 공개 예정!";

  const dayBefore = new Date(eventStartTime);
  dayBefore.setDate(dayBefore.getDate() - 1);
  dayBefore.setHours(16, 20, 0, 0);

  const now = new Date();
  if (now >= dayBefore) {
    return "누구를 우연처럼 만나게 될까요?";
  }

  return `${formatDateToMonthDay(dayBefore.toISOString())} 전체 공개!`;
}

function getCurationNotesUnlockDate(eventStartTime: string | undefined): string {
  if (!eventStartTime) return "전체 공개 예정!";

  const eventDay = new Date(eventStartTime);
  eventDay.setHours(7, 30, 0, 0);

  const now = new Date();
  if (now >= eventDay) {
    return "오늘은 이렇게 준비했어요.";
  }

  return `${formatDateToMonthDay(eventStartTime)} 전체 공개!`;
}

interface ReservationDetailViewItemsProps {
  reservation: GetMyUpcomingReservationsResponseDto;
  event?: GetEventResponseDto;
  isPast?: boolean;
}

const PIE_CHART_MAX_SIZE = 200;

function DistributionPieChart({
  data,
}: {
  data: { name: string; value: number; color: string }[];
}) {
  const { ref, width } = useElementSize();
  const size = Math.max(1, Math.min(PIE_CHART_MAX_SIZE, Math.floor(width)));

  return (
    <Box
      ref={ref}
      style={{ flex: "1 1 auto", minWidth: 0, maxWidth: PIE_CHART_MAX_SIZE, marginLeft: "auto" }}
    >
      {width > 0 && (
        <Suspense fallback={<Loader />}>
          <PieChart withTooltip tooltipDataSource="segment" size={size} data={data} />
        </Suspense>
      )}
    </Box>
  );
}

const borderBoxStyle = {
  border: "1px solid gray",
  borderRadius: "10px",
  padding: "5px",
};

export const ReservationDetailItems = ({
  reservation,
  event,
  isPast = false,
}: ReservationDetailViewItemsProps) => {
  const [isFeedbackModalOpen, { open: openFeedbackModal, close: closeFeedbackModal }] =
    useDisclosure(false);
  const {
    currentPoint: randomTalkingPoint,
    isExhausted: isTalkingPointsExhausted,
    next: nextTalkingPoint,
    isPending: isLoadingRandomTalkingPoint,
    isError: isErrorRandomTalkingPoint,
  } = useGetRandomTalkingPoints();

  // when deep linked to the feedback form, we should just open the modal for them.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "#event-detail-feedback-form" && event?.feedbackStatus !== "SUBMITTED") {
      openFeedbackModal();
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [openFeedbackModal, event?.feedbackStatus]);

  const groupHintsHeaderText = getGroupHintsUnlockDate(event?.startTime);
  const curationNotesHeaderText = getCurationNotesUnlockDate(event?.startTime);

  return (
    <>
      <LineHeader badgeText="그룹 힌트" headerText={groupHintsHeaderText} />
      <LineItem
        headerText="🪄  우리 테이블에는 어떤 사람이 있을까요?"
        timeText="4:20PM"
        isUnlocked={Boolean(event?.guestMessages?.length ?? 0 > 0)}
      >
        <Stack>
          {event?.guestMessages?.map((message) => (
            <LineItemSubItem key={message} text={message} />
          ))}
        </Stack>
      </LineItem>
      <LineItem
        headerText="🍰 어떤 분야가 함께 할까요?"
        timeText="4:20PM"
        isUnlocked={Boolean(event?.professionDistribution?.length ?? 0 > 0)}
      >
        <Box style={{ ...borderBoxStyle }} bg="white" p="md">
          <Flex gap="md" align="flex-start">
            <Stack gap="xs" style={{ flex: "0 0 auto" }}>
              {event?.professionDistribution?.map((item, index) => (
                <Group key={item.profession} gap={4}>
                  <Box
                    w={12}
                    h={12}
                    style={{
                      backgroundColor: NICE_COLORS[index % NICE_COLORS.length],
                      borderRadius: 2,
                    }}
                  />
                  <Text size="sm" c="black">
                    {PROFESSION_DISPLAY_MAP[item.profession] || item.profession}: {item.percentage}%
                  </Text>
                </Group>
              ))}
            </Stack>
            <DistributionPieChart
              data={
                event?.professionDistribution?.map((item, index) => ({
                  name: PROFESSION_DISPLAY_MAP[item.profession] || item.profession,
                  value: item.percentage,
                  color: NICE_COLORS[index % NICE_COLORS.length],
                })) ?? []
              }
            />
          </Flex>
        </Box>
      </LineItem>
      <LineItem
        headerText="🌏 어떤 국적이 함께 할까요?"
        timeText="4:20PM"
        isUnlocked={Boolean(event?.nationalityDistribution?.length ?? 0 > 0)}
      >
        <Box style={{ ...borderBoxStyle }} bg="white" p="md">
          <Flex gap="md" align="flex-start">
            <Stack gap="xs" style={{ flex: "0 0 auto" }}>
              {event?.nationalityDistribution?.map((item, index) => (
                <Group key={item.nationality} gap={4}>
                  <Box
                    w={12}
                    h={12}
                    style={{
                      backgroundColor: NICE_COLORS_2[index % NICE_COLORS_2.length],
                      borderRadius: 2,
                    }}
                  />
                  <Text size="sm" c="black">
                    {item.nationality}: {item.percentage}%
                  </Text>
                </Group>
              ))}
            </Stack>
            <DistributionPieChart
              data={
                event?.nationalityDistribution?.map((item, index) => ({
                  name: item.nationality,
                  value: item.percentage,
                  color: NICE_COLORS_2[index % NICE_COLORS_2.length],
                })) ?? []
              }
            />
          </Flex>
        </Box>
      </LineItem>
      <LineHeader badgeText="큐레이션 노트" headerText={curationNotesHeaderText} />
      <LineItem
        headerText="📍 이번 주 선정한 레스토랑"
        timeText="7:30AM"
        isUnlocked={Boolean(event?.restaurant?.address)}
      >
        <Box style={borderBoxStyle}>
          {event?.restaurant?.name && (
            <Text ta="center" c="white" fw={700} mb="xs">
              {event.restaurant.name}
            </Text>
          )}
          <Text ta="center" c="white">
            {event?.restaurant?.address}
          </Text>
        </Box>
        <AttendanceStatusSelector
          reservationId={reservation.id}
          currentStatus={reservation.attendanceStatus}
          isPast={isPast}
        />
      </LineItem>
      <LineItem
        headerText="✉️ 큐레이터 노트"
        timeText="6:00PM"
        isUnlocked={Boolean(event?.curatorMessage)}
      >
        <Box style={borderBoxStyle}>
          <Text ta="left" c="white" style={{ whiteSpace: "pre-wrap" }} p="xs">
            {event?.curatorMessage}
          </Text>
        </Box>
      </LineItem>
      <LineItem
        headerText="🎁 이야기 카드"
        timeText="6:00PM"
        isUnlocked={Boolean(event?.talkingPointsCardLink)}
      >
        <Box style={borderBoxStyle}>
          <Text ta="center" c="white" style={{ whiteSpace: "pre-wrap" }} p="xs">
            {isErrorRandomTalkingPoint
              ? "랜덤 질문을 가져오는데 실패했습니다."
              : isTalkingPointsExhausted
                ? "모든 질문을 확인했습니다!"
                : (randomTalkingPoint ?? "랜덤 질문을 가져오는 중입니다...")}
          </Text>
        </Box>
        <Group w="100%" justify="space-around">
          <Button
            variant="outline"
            color="white"
            onClick={nextTalkingPoint}
            loading={isLoadingRandomTalkingPoint}
            disabled={isTalkingPointsExhausted || isPast}
            flex={1}
          >
            {isTalkingPointsExhausted ? "질문 완료" : "랜덤 질문"}
          </Button>
          {isPast ? (
            <Button variant="outline" color="white" flex={1} disabled>
              전체 보기
            </Button>
          ) : (
            <Button
              component={NavLink}
              target="_blank"
              to={event?.talkingPointsCardLink ?? ""}
              variant="outline"
              color="white"
              flex={1}
            >
              전체 보기
            </Button>
          )}
        </Group>
      </LineItem>
      <LineItem
        headerText="🥂 이번주 애프터 장소"
        timeText="8:00PM"
        isUnlocked={Boolean(event?.afterPartyVenue?.address)}
      >
        <Box style={borderBoxStyle}>
          {event?.afterPartyVenue?.name && (
            <Text ta="center" c="white" fw={700} mb="xs">
              {event.afterPartyVenue.name}
            </Text>
          )}
          <Text ta="center" c="white">
            {event?.afterPartyVenue?.address}
          </Text>
        </Box>
      </LineItem>
      <LineHeader badgeText="피드백" headerText="오늘 오프 어떠셨나요?" />
      <LineItem
        id="event-detail-feedback-form"
        headerText="오늘 함께한 그룹, 내 취향과 잘 맞았나요?"
        timeText="9:55PM"
        isUnlocked={event?.feedbackStatus !== "NOT_READY"}
      >
        {event?.feedbackStatus === "SUBMITTED" ? (
          <Box style={borderBoxStyle}>
            <Text ta="center" c="white">
              피드백을 남겨주셔서 감사합니다!
            </Text>
          </Box>
        ) : (
          <>
            <Box style={borderBoxStyle}>
              <Text ta="center" c="white">
                오늘 경험 어떠셨는지 피드백 부탁드려도 될까요? 🥳
                <br />
                <br />
                소중한 피드백 덕분에 오프의 큐레이션도 한 뼘 더 성장해요! 🌱
              </Text>
            </Box>
            <Center>
              <StyledActionButton
                onClick={openFeedbackModal}
                size="sm"
                textSize="md"
                style={{ border: "1px solid black", marginTop: "10px" }}
              >
                피드백 남기기
              </StyledActionButton>
            </Center>
          </>
        )}
      </LineItem>
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={closeFeedbackModal}
        eventId={reservation.eventId as number}
      />
    </>
  );
};
